import React from 'react'
import { StyleSheet, css } from 'aphrodite'
import { AppTheme } from '../../styles/AppTheme'

function asArray(value) {
  return Array.isArray(value) ? value : []
}

function asText(value, fallback = '') {
  if (value == null) return fallback
  const out = String(value).trim()
  return out || fallback
}

class FlowNodeRenderer extends React.PureComponent {
  getFieldValue = (name, defaultValue = '') => {
    if (!name) return defaultValue
    const values = this.props.values || {}
    return values[name] != null ? values[name] : defaultValue
  }

  isFieldError = (name) => {
    const errors = this.props.errors || {}
    return Boolean(name && errors[name])
  }

  renderFieldError = (name) => {
    if (!this.isFieldError(name)) return null
    const errors = this.props.errors || {}
    return <div className={css(styles.fieldError)}>{errors[name]}</div>
  }

  onTextInputChange = (name, value) => {
    if (!name || !this.props.onChange) return
    this.props.onChange(name, value)
  }

  onRadioChange = (name, value) => {
    if (!name || !this.props.onChange) return
    this.props.onChange(name, value)
  }

  onCheckboxToggle = (name, value) => {
    if (!name || !this.props.onChange) return
    const current = Array.isArray(this.getFieldValue(name, [])) ? this.getFieldValue(name, []) : []
    const exists = current.includes(value)
    const next = exists ? current.filter(v => v !== value) : [...current, value]
    this.props.onChange(name, next)
  }

  renderLabel = (label) => {
    const text = asText(label)
    if (!text) return null
    return <div className={css(styles.fieldLabel)}>{text}</div>
  }

  renderGroupLabel = (node) => {
    const label = asText(node.label || node.title)
    if (!label) return null
    const required = Boolean(node.required)
    return (
      <div className={css(styles.fieldLabel)}>
        {label}
        {!required && <span className={css(styles.optionalSuffix)}> (optional)</span>}
      </div>
    )
  }

  renderOptions = (node) => {
    const options = asArray(node.data_source || node['data-source'] || node.options || node.items)
    if (!options.length) return null
    const type = asText(node.type).toLowerCase()
    const name = asText(node.name)
    const currentValue = this.getFieldValue(name, type === 'checkboxgroup' ? [] : '')

    if (type === 'radiobuttonsgroup') {
      return (
        <div className={css(styles.optionsWrap)}>
          {options.map((opt, idx) => {
            const optionValue = asText(opt && (opt.id || opt.value || opt.title || opt.label), `option_${idx + 1}`)
            const label = asText(opt && (opt.title || opt.label || opt.value || opt.id), `Option ${idx + 1}`)
            const selected = currentValue === optionValue
            return (
              <div key={`opt_${idx}`} className={css(styles.optionRow)} onClick={() => this.onRadioChange(name, optionValue)}>
                <span className={css(styles.optionRowLabel)}>{label}</span>
                <span className={css(styles.radioOuter, selected && styles.radioOuterSelected)}>
                  {selected && <span className={css(styles.radioInner)} />}
                </span>
              </div>
            )
          })}
        </div>
      )
    }

    if (type === 'checkboxgroup') {
      const currentValues = Array.isArray(currentValue) ? currentValue : []
      return (
        <div className={css(styles.optionsWrap)}>
          {options.map((opt, idx) => {
            const optionValue = asText(opt && (opt.id || opt.value || opt.title || opt.label), `option_${idx + 1}`)
            const label = asText(opt && (opt.title || opt.label || opt.value || opt.id), `Option ${idx + 1}`)
            const selected = currentValues.includes(optionValue)
            return (
              <div key={`opt_${idx}`} className={css(styles.optionRow)} onClick={() => this.onCheckboxToggle(name, optionValue)}>
                <span className={css(styles.optionRowLabel)}>{label}</span>
                <span className={css(styles.checkboxOuter, selected && styles.checkboxOuterSelected)}>
                  {selected && <i className={css(styles.checkboxTick)}>check</i>}
                </span>
              </div>
            )
          })}
        </div>
      )
    }

    return null
  }

  renderComponent = (node, keyPrefix = 'node') => {
    if (!node || typeof node !== 'object') return null
    const type = asText(node.type).toLowerCase()
    const key = `${keyPrefix}_${type || 'unknown'}`

    if (type === 'footer') {
      if (this.props.hideFooter) return null
      const label = asText(node.label || node.text || node.title, 'Continue')
      const canSubmit = Boolean(this.props.canSubmit)
      return (
        <button
          key={key}
          className={css(styles.footerButton, !canSubmit && styles.footerButtonDisabled)}
          type='button'
          aria-disabled={!canSubmit}
          onClick={() => this.props.onSubmit && this.props.onSubmit()}
        >
          {label}
        </button>
      )
    }

    if (type === 'textinput') {
      const required = Boolean(node.required)
      const placeholder = asText(node.placeholder || node.hint, required ? '' : 'Optional')
      const name = asText(node.name)
      const value = this.getFieldValue(name, '')
      const showError = this.isFieldError(name)
      const showSuccess = required && !showError && String(value || '').trim() !== ''
      return (
        <div key={key} className={css(styles.fieldWrap)}>
          {this.renderLabel(node.label || node.title)}
          <input
            type='text'
            value={value}
            placeholder={placeholder}
            className={css(
              styles.inputMock,
              showError && styles.inputMockError,
              showSuccess && styles.inputMockSuccess
            )}
            onChange={e => this.onTextInputChange(name, e.target.value)}
          />
          {this.renderFieldError(name)}
        </div>
      )
    }

    if (type === 'radiobuttonsgroup' || type === 'checkboxgroup') {
      const name = asText(node.name)
      return (
        <div key={key} className={css(styles.fieldWrap)}>
          {this.renderGroupLabel(node)}
          {this.renderFieldError(name)}
          {this.renderOptions(node)}
        </div>
      )
    }

    const children = asArray(node.children || node.components)
    const isContainer = ['form', 'layout', 'singlecolumnlayout', 'section', 'container', 'screen'].includes(type)
      || children.length > 0
    if (isContainer) {
      return (
        <div key={key} className={css(type === 'form' ? styles.formWrap : styles.containerWrap)}>
          {children.map((child, idx) => this.renderComponent(child, `${key}_${idx}`))}
        </div>
      )
    }

    return null
  }

  render() {
    const { rootNode } = this.props
    return this.renderComponent(rootNode)
  }
}

export class WhatsappFlowPreview extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      values: {},
      errors: {},
    }
  }

  getScreens = () => {
    return asArray(this.props.flowScreens)
  }

  collectFieldsFromNode = (node, out = []) => {
    if (!node || typeof node !== 'object') return out
    const type = asText(node.type).toLowerCase()
    const isField = ['textinput', 'radiobuttonsgroup', 'checkboxgroup'].includes(type)
    if (isField) {
      out.push({
        name: asText(node.name),
        label: asText(node.label || node.title || node.name, 'field'),
        required: Boolean(node.required),
        type,
      })
    }
    const children = asArray(node.children || node.components)
    children.forEach(child => this.collectFieldsFromNode(child, out))
    return out
  }

  hasValue = (field, values) => {
    const val = values[field.name]
    if (field.type === 'checkboxgroup') return Array.isArray(val) && val.length > 0
    return val != null && String(val).trim() !== ''
  }

  validateRequiredFields = (screen) => {
    const layout = screen && screen.layout ? screen.layout : {}
    const fields = this.collectFieldsFromNode(layout, [])
    const errors = {}
    fields.forEach((field) => {
      if (!field.required || !field.name) return
      if (!this.hasValue(field, this.state.values)) {
        if (field.type === 'radiobuttonsgroup' || field.type === 'checkboxgroup') {
          errors[field.name] = 'Select an option'
        } else {
          errors[field.name] = `Enter ${field.label}`
        }
      }
    })
    return errors
  }

  onFieldChange = (name, value) => {
    this.setState(prev => {
      const nextValues = { ...(prev.values || {}), [name]: value }
      const nextErrors = { ...(prev.errors || {}) }
      if (nextErrors[name]) {
        const hasAny = value != null && (Array.isArray(value) ? value.length > 0 : String(value).trim() !== '')
        if (hasAny) delete nextErrors[name]
      }
      return {
        values: nextValues,
        errors: nextErrors,
      }
    })
  }

  onSubmit = (screen) => {
    const errors = this.validateRequiredFields(screen)
    this.setState({ errors })
  }

  canSubmit = (screen) => {
    const errors = this.validateRequiredFields(screen)
    return Object.keys(errors).length === 0
  }

  findFooterNode = (node) => {
    if (!node || typeof node !== 'object') return null
    const type = asText(node.type).toLowerCase()
    if (type === 'footer') return node
    const children = asArray(node.children || node.components)
    for (let i = 0; i < children.length; i += 1) {
      const hit = this.findFooterNode(children[i])
      if (hit) return hit
    }
    return null
  }

  renderScreen = (screen, idx) => {
    const title = asText(screen && (screen.title || screen.name || screen.id), `Screen ${idx + 1}`)
    const layout = screen && (screen.layout || {})
    const footerNode = this.findFooterNode(layout)
    const footerLabel = asText(footerNode && (footerNode.label || footerNode.text || footerNode.title), 'Continue')
    const canSubmit = this.canSubmit(screen)
    return (
      <div key={`screen_${idx}`} className={css(styles.screenCard)}>
        <div className={css(styles.screenHeader)}>
          <div className={css(styles.screenTitle)}>{title}</div>
        </div>
        <div className={css(styles.screenBody)}>
          <FlowNodeRenderer
            rootNode={layout}
            values={this.state.values}
            errors={this.state.errors}
            onChange={this.onFieldChange}
            onSubmit={() => this.onSubmit(screen)}
            canSubmit={canSubmit}
            hideFooter
          />
        </div>
        {footerNode && (
          <div className={css(styles.screenFooter)}>
            <button
              type='button'
              className={css(styles.footerButton, !canSubmit && styles.footerButtonDisabled)}
              aria-disabled={!canSubmit}
              onClick={() => this.onSubmit(screen)}
            >
              {footerLabel}
            </button>
          </div>
        )}
      </div>
    )
  }

  render() {
    const screens = this.getScreens()
    const hasMultipleScreens = screens.length > 1

    return (
      <div className={css(styles.phoneShell)}>
        <div className={css(styles.bodyArea, hasMultipleScreens && styles.bodyAreaScrollable)}>
          {screens.length > 0
            ? screens.map(this.renderScreen)
            : <div className={css(styles.emptyState)}>No preview data found for this flow.</div>}
        </div>
      </div>
    )
  }
}

const styles = StyleSheet.create({
  phoneShell: {
    width: 360,
    maxWidth: '100%',
    margin: '0 auto',
    borderRadius: 24,
    backgroundColor: '#e5ddd5',
    border: '1px solid #d1d7db',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    display: 'flex',
    flexDirection: 'column',
  },
  bodyArea: {
    padding: 12,
    maxHeight: 'min(560px, calc(100vh - 250px))',
    overflowY: 'hidden',
    backgroundImage: 'radial-gradient(#ffffff66 1px, transparent 1px)',
    backgroundSize: '8px 8px',
  },
  bodyAreaScrollable: {
    overflowY: 'auto',
  },
  screenCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    border: '1px solid #e6e8ea',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: 10,
    height: 'min(560px, calc(100vh - 274px))',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  screenHeader: {
    flex: '0 0 auto',
    padding: 15,
    borderBottom: '1px solid #eceff1',
    backgroundColor: '#fff',
    zIndex: 1,
  },
  screenBody: {
    flex: '1 1 auto',
    overflowY: 'auto',
    padding: 12,
  },
  screenFooter: {
    flex: '0 0 auto',
    padding: '10px 12px 12px',
    borderTop: '1px solid #eceff1',
    backgroundColor: '#fff',
  },
  screenTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#0A0A0A',
    textAlign: 'center',
  },
  containerWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  formWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  fieldWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  fieldLabel: {
    fontSize: 14,
    color: '#0A0A0A',
    fontWeight: 600,
  },
  inputMock: {
    border: '1px solid #d3d8de',
    borderRadius: 8,
    minHeight: 40,
    padding: '5px 5px',
    fontSize: 13,
    color: '#111b21',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    outline: 'none',
  },
  inputMockError: {
    border: '1px solid #dc2626',
  },
  inputMockSuccess: {
    border: '1px solid #1DAA61',
  },
  fieldError: {
    color: '#dc2626',
    fontSize: 12,
    marginTop: 2,
  },
  optionalSuffix: {
    color: '#6b7280',
    fontWeight: 400,
  },
  optionsWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  optionRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    minHeight: 34,
  },
  optionRowLabel: {
    fontSize: 15,
    color: '#111b21',
  },
  radioOuter: {
    width: 15,
    height: 15,
    borderRadius: '50%',
    border: '2px solid #6b7280',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSelected: {
    borderColor: '#1DAA61',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#1DAA61',
  },
  checkboxOuter: {
    width: 15,
    height: 15,
    borderRadius: 2,
    border: '2px solid #6b7280',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxOuterSelected: {
    borderColor: '#1DAA61',
    backgroundColor: '#1DAA61',
  },
  checkboxTick: {
    fontFamily: 'Material Icons Outlined',
    fontStyle: 'normal',
    color: '#fff',
    fontSize: 14,
    lineHeight: 1,
  },
  footerButton: {
    width: '100%',
    border: 'none',
    borderRadius: 8,
    height: 40,
    fontSize: 14,
    color: '#fff',
    fontWeight: 500,
    backgroundColor: '#1DAA61',
    cursor: 'pointer',
  },
  footerButtonDisabled: {
    backgroundColor: AppTheme.secondaryColor,
    color: '#808080',
    border: '1px solid #E5E5E5',
    boxShadow: 'none',
  },
  emptyState: {
    borderRadius: 8,
    border: '1px dashed #c8ced6',
    backgroundColor: '#fff',
    color: '#64748b',
    fontSize: 13,
    textAlign: 'center',
    padding: '20px 14px',
  },
})

export default WhatsappFlowPreview
